import { useMemo } from "react";
import type { EChartsOption } from "echarts";
import AppChart from "../charts/AppChart";
import { formatCurrency } from "../../utils/currencyUtils";
import {
  KioskTrendGranularity,
  KioskTransactionsSummary,
} from "../../types/kioskTransactions.type";

interface KioskPerformanceSummaryCardsProps {
  summary: KioskTransactionsSummary;
  isKioskOwner?: boolean;
  trendGranularity: KioskTrendGranularity;
  onTrendGranularityChange: (value: KioskTrendGranularity) => void;
  fromDate?: string;
  toDate?: string;
  loading?: boolean;
  error?: string | null;
}

const cardClassName =
  "rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-slate-900 p-4";

export const KioskPerformanceSummaryCards = ({
  summary,
  isKioskOwner = false,
  trendGranularity,
  onTrendGranularityChange,
  fromDate,
  toDate,
  loading,
  error,
}: KioskPerformanceSummaryCardsProps) => {
  const hasStatusData = summary.kpis.successful + summary.kpis.failed > 0;
  const trendSeries = summary.trends[trendGranularity] || [];

  // Derive chart granularity automatically from the applied date range
  const donationChartGranularity = useMemo<KioskTrendGranularity>(() => {
    if (!fromDate || !toDate) return "daily";
    const from = new Date(fromDate).getTime();
    const to = new Date(toDate).getTime();
    if (isNaN(from) || isNaN(to)) return "daily";
    const days = (to - from) / (1000 * 60 * 60 * 24);
    if (days < 30) return "daily";
    if (days < 90) return "weekly";
    if (days < 365) return "monthly";
    return "monthly"; // yearly rolled up from monthly below
  }, [fromDate, toDate]);

  const isYearly = useMemo(() => {
    if (!fromDate || !toDate) return false;
    const days = (new Date(toDate).getTime() - new Date(fromDate).getTime()) / (1000 * 60 * 60 * 24);
    return days >= 365;
  }, [fromDate, toDate]);

  // For yearly: roll up monthly buckets into year buckets
  const yearlyBuckets = useMemo(() => {
    const monthly = summary.trends.monthly || [];
    const map = new Map<string, { totalUnits: number; totalUsd: number }>();
    for (const point of monthly) {
      const year = point.bucket.slice(0, 4);
      const existing = map.get(year) ?? { totalUnits: 0, totalUsd: 0 };
      map.set(year, {
        totalUnits: existing.totalUnits + point.totalUnits,
        totalUsd: existing.totalUsd + point.totalUsd,
      });
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([bucket, values]) => ({ bucket, ...values }));
  }, [summary.trends.monthly]);

  const donationTrendSeries = useMemo(() => {
    if (isYearly) return yearlyBuckets;
    return summary.trends[donationChartGranularity] || [];
  }, [isYearly, yearlyBuckets, donationChartGranularity, summary.trends]);

  const donationChartLabel = useMemo(() => {
    if (isYearly) return "Yearly";
    return donationChartGranularity.charAt(0).toUpperCase() + donationChartGranularity.slice(1);
  }, [isYearly, donationChartGranularity]);

  const statusChartOption = useMemo<EChartsOption>(() => {
    return {
      tooltip: { trigger: "item" },
      series: [
        {
          type: "pie",
          radius: ["42%", "70%"],
          avoidLabelOverlap: false,
          label: { show: false },
          data: [
            { value: summary.kpis.successful, name: "Received", itemStyle: { color: "#10B981" } },
            { value: summary.kpis.failed, name: "Incomplete", itemStyle: { color: "#EF4444" } },
          ],
        },
      ],
    };
  }, [summary]);

  const donationByPeriodChartOption = useMemo<EChartsOption>(() => {
    return {
      tooltip: {
        trigger: "axis",
        formatter: (params: any) => {
          const p = Array.isArray(params) ? params[0] : params;
          return `${p.axisValue}<br/><b>$${Number(p.value ?? 0).toFixed(2)}</b>`;
        },
      },
      grid: { left: 12, right: 12, top: 24, bottom: 36, containLabel: true },
      xAxis: {
        type: "category",
        data: donationTrendSeries.map((point) => point.bucket),
        axisLabel: {
          rotate: donationTrendSeries.length > 8 ? 20 : 0,
          interval: 0,
          formatter: (value: string) =>
            value.length > 10 ? `${value.slice(0, 10)}...` : value,
        },
      },
      yAxis: {
        type: "value",
        axisLabel: { formatter: (v: number) => `$${v.toFixed(0)}` },
      },
      series: [
        {
          type: "bar",
          data: donationTrendSeries.map((point) => point.totalUsd),
          itemStyle: { color: "#10B981" },
          barMaxWidth: 40,
        },
      ],
    };
  }, [donationTrendSeries]);

  const impactTrendOption = useMemo<EChartsOption>(() => {
    return {
      tooltip: { trigger: "axis" },
      grid: { left: 12, right: 12, top: 24, bottom: 36, containLabel: true },
      xAxis: {
        type: "category",
        data: trendSeries.map((point) => point.bucket),
        axisLabel: {
          interval: 0,
          formatter: (value: string) => {
            if (trendGranularity === "daily") {
              return value.slice(5);
            }
            return value;
          },
        },
      },
      yAxis: {
        type: "value",
        axisLabel: {
          formatter: (value: number) => `$${Number(value).toFixed(0)}`,
        },
      },
      series: [
        {
          type: "line",
          smooth: true,
          data: trendSeries.map((point) => point.totalUsd),
          itemStyle: { color: "#0EA5E9" },
          lineStyle: { width: 3, color: "#0EA5E9" },
          areaStyle: {
            color: "rgba(14, 165, 233, 0.18)",
          },
        },
      ],
    };
  }, [trendSeries, trendGranularity]);

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={index} className={`${cardClassName} h-28 animate-pulse`} />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className={cardClassName}>
          <p className="text-sm text-gray-600 dark:text-gray-300">Completed Donations</p>
          <p className="text-2xl font-semibold mt-1 text-green-600 dark:text-green-400">
            {summary.kpis.successful}
          </p>
        </div>
        {!isKioskOwner && (
          <div className={cardClassName}>
            <p className="text-sm text-gray-600 dark:text-gray-300">Failed Donations</p>
            <p className="text-2xl font-semibold mt-1 text-red-600 dark:text-red-400">
              {summary.kpis.failed}
            </p>
          </div>
        )}
        <div className={cardClassName}>
          <p className="text-sm text-gray-600 dark:text-gray-300">Total FOOD Bars</p>
          <p className="text-2xl font-semibold mt-1">{summary.impact.totalFoodBars}</p>
        </div>
        <div className={cardClassName}>
          <p className="text-sm text-gray-600 dark:text-gray-300">Total Impact (USD)</p>
          <p className="text-2xl font-semibold mt-1">
            {formatCurrency(summary.impact.totalAmountUsd, "USD")}
          </p>
        </div>
        {isKioskOwner && (
          <div className={cardClassName}>
            <p className="text-sm text-gray-600 dark:text-gray-300">Reward Balance (USD)</p>
            <p className="text-2xl font-semibold mt-1 text-emerald-700 dark:text-emerald-300">
              {formatCurrency(summary.ownerRewards.balanceUsd, "USD")}
            </p>
          </div>
        )}
      </div>

      <div className={`grid grid-cols-1 ${isKioskOwner ? "" : "xl:grid-cols-2"} gap-4`}>
        {!isKioskOwner && (
          <div className={cardClassName}>
            <div className="mb-3 flex items-center justify-between">
              <p className="font-medium text-gray-900 dark:text-gray-100">Status Distribution</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Success vs failed
              </p>
            </div>
            {hasStatusData ? (
              <AppChart option={statusChartOption} height={260} />
            ) : (
              <div className="h-[260px] rounded-xl border border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
                No data
              </div>
            )}
          </div>
        )}
        <div className={cardClassName}>
          <div className="mb-3 flex items-center justify-between">
            <p className="font-medium text-gray-900 dark:text-gray-100">Donation Amounts</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {donationChartLabel} · Total {formatCurrency(summary.impact.totalAmountUsd, "USD")}
            </p>
          </div>
          {donationTrendSeries.length > 0 ? (
            <AppChart option={donationByPeriodChartOption} height={260} />
          ) : (
            <div className="h-[260px] rounded-xl border border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
              No data
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <div className={cardClassName}>
          <p className="text-sm text-gray-600 dark:text-gray-300">Digital Channel Impact</p>
          <p className="text-2xl font-semibold mt-1">
            {summary.impact.byChannel.DIGITAL.foodBars} bars
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            {formatCurrency(summary.impact.byChannel.DIGITAL.amountUsd, "USD")}
          </p>
        </div>
        <div className={cardClassName}>
          <p className="text-sm text-gray-600 dark:text-gray-300">Physical Channel Impact</p>
          <p className="text-2xl font-semibold mt-1">
            {summary.impact.byChannel.PHYSICAL.foodBars} bars
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
            {formatCurrency(summary.impact.byChannel.PHYSICAL.amountUsd, "USD")}
          </p>
        </div>
        <div className={cardClassName}>
          <p className="text-sm text-gray-600 dark:text-gray-300">Reward Balance (USD)</p>
          <p className="text-2xl font-semibold mt-1">
            {formatCurrency(summary.ownerRewards.balanceUsd, "USD")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Generated {formatCurrency(summary.ownerRewards.totalRewardsUsd, "USD")} | Fees still due {formatCurrency(summary.ownerRewards.totalPayableUsd, "USD")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2 leading-5">
            Reward balance = commission generated above the monthly licence fee, minus any rewards already redeemed.
          </p>
        </div>
      </div>

      <div className={cardClassName}>
        <div className="mb-3 flex items-center justify-between gap-3">
          <p className="font-medium text-gray-900 dark:text-gray-100">Impact Trend</p>
          <select
            value={trendGranularity}
            onChange={(event) => onTrendGranularityChange(event.target.value as KioskTrendGranularity)}
            className="rounded-md border border-gray-300 px-2 py-1 text-sm dark:border-white/15 dark:bg-slate-800"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
        {trendSeries.length > 0 ? (
          <AppChart option={impactTrendOption} height={260} />
        ) : (
          <div className="h-[260px] rounded-xl border border-dashed border-gray-200 dark:border-white/10 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">
            No trend data
          </div>
        )}
      </div>

      <div className={cardClassName}>
        <div className="mb-3 flex items-center justify-between">
          <p className="font-medium text-gray-900 dark:text-gray-100">Topline Donors</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">Top 5 by USD impact</p>
        </div>
        {summary.topDonors.length === 0 ? (
          <div className="text-sm text-gray-500 dark:text-gray-400">No donor data available.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 dark:text-gray-400">
                  <th className="py-2 pr-3">Donor</th>
                  <th className="py-2 pr-3 text-right">Amount</th>
                  <th className="py-2 pr-3">Date / Time</th>
                  <th className="py-2">Location</th>
                </tr>
              </thead>
              <tbody>
                {summary.topDonors.slice(0, 5).map((donor) => (
                  <tr key={`${donor.donor}-${donor.lastDonationAt || "-"}`} className="border-t border-gray-200 dark:border-white/10">
                    <td className="py-2 pr-3">{donor.donor}</td>
                    <td className="py-2 pr-3 text-right">{formatCurrency(donor.amountUsd, "USD")}</td>
                    <td className="py-2 pr-3">{donor.lastDonationAt ? new Date(donor.lastDonationAt).toLocaleString() : "-"}</td>
                    <td className="py-2">{donor.location || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default KioskPerformanceSummaryCards;
